import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ArrowLeftOutlined } from "@/config/antd";
import Back from "@/components/specific/Back";
import Input from "@/components/common/inputs/Input";
import TextEditor from "@/components/common/inputs/Text_Editor";
import FileInput from "@/components/common/inputs/File_Input";
import Button from "@/components/common/Button";
import Success from "@/components/common/alerts/Success";
import { useLoader } from "@/hooks/LoaderProvider";
import DOMPurify from "dompurify";
import Error from "@/components/common/alerts/Error";
import Steps from "@/components/common/Steps";
import { getOrganizationId } from "@/utils/token_functions";

import {
  updateManifestoForm,
  resetManifestoForm,
  createManifesto,
  selectManifestoForm,
  updateManifesto,
  fetchSingleManifesto,
} from "@/store/redux/slices/manifesto/manifesto_slice";
import { uploadFile } from "@/store/redux/slices/user/file_upload_slice";
import Breadcrumb from "@/components/specific/Breadcrumb";

const AddNewManifesto = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const { showLoader, hideLoader } = useLoader();

  const manifestoForm = useSelector(selectManifestoForm);

  const [isSuccessVisible, setIsSuccessVisible] = useState(false);
  const [uploadedFile] = useState(null);
  const [isErrorVisible, setIsErrorVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState({
    header: "",
    paragraph: "",
  });

  // Steps related state
  const formSections = [
    { title: "Manifesto Information" },
    { title: "Media Upload" },
    { title: "Campaigns" },
  ];

  const [currentSection, setCurrentSection] = useState(0);
  const [sectionsCompleted, setSectionsCompleted] = useState([]);

  const sectionRefs = useRef([]);

  // Function to check which section is currently most visible
  useEffect(() => {
    const handleScroll = () => {
      const viewportHeight = window.innerHeight;
      const viewportCenter = window.scrollY + viewportHeight / 2;

      // Find the section closest to the center of the viewport
      let closestSection = 0;
      let closestDistance = Infinity;

      sectionRefs.current.forEach((sectionElement, index) => {
        if (sectionElement) {
          const rect = sectionElement.getBoundingClientRect();
          const sectionCenter = window.scrollY + rect.top + rect.height / 2;
          const distance = Math.abs(viewportCenter - sectionCenter);

          if (distance < closestDistance) {
            closestDistance = distance;
            closestSection = index;
          }
        }
      });

      setCurrentSection(closestSection);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Call once on mount to set initial state

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Check if sections are completed
  const checkSectionCompletion = useCallback(() => {
    const completed = [];

    // Section 0: Manifesto Information
    if (manifestoForm.name && manifestoForm.description) {
      completed.push(0);
    }

    // Section 1: Media Upload
    if (manifestoForm.fileUrl || uploadedFile) {
      completed.push(1);
    }

    // Section 2: Campaigns (optional in this case)
    // Since campaigns are optional, we can consider it always complete
    // Uncomment this if you want to have specific validation for campaigns
    // completed.push(2);

    setSectionsCompleted(completed);
  }, [manifestoForm, uploadedFile]);

  // Run section completion check when form data changes
  useEffect(() => {
    checkSectionCompletion();
  }, [manifestoForm, uploadedFile, checkSectionCompletion]);

  // Load manifesto data if in edit mode
  useEffect(() => {
    const loadManifestoData = async () => {
      if (isEditMode) {
        try {
          showLoader();
          const result = await dispatch(
            fetchSingleManifesto({ key: "_id", value: id })
          ).unwrap();
          console.log("result", result);
          if (result) {
            dispatch(
              updateManifestoForm({
                _id: result._id,
                name: result.name,
                description: result.description,
                fileUrl: result.fileUrl,
              })
            );
          }
        } catch (error) {
          console.error("Failed to fetch manifesto:", error);
        } finally {
          hideLoader();
        }
      }
    };

    loadManifestoData();
  }, [dispatch, hideLoader, id, isEditMode, showLoader]);

  const handleFileUpload = (file) => {
    dispatch(
      updateManifestoForm({
        fileUrl: file,
      })
    );
  };

  const handleSubmit = async () => {
    try {
      if (!manifestoForm.fileUrl) {
        setErrorMessage({
          header: "Missing File",
          paragraph: "Please upload a file before proceeding.",
        });
        setIsErrorVisible(true);
        return;
      }

      showLoader();

      let fileUrl = manifestoForm.fileUrl;
      if (manifestoForm.fileUrl instanceof File) {
        try {
          const uploadResponse = await dispatch(
            uploadFile({ file: manifestoForm.fileUrl, folder: "manifestos" })
          ).unwrap();
          fileUrl = uploadResponse.fileUrl;
        } catch (uploadError) {
          console.error("File upload failed:", uploadError);
          hideLoader();
          setErrorMessage({
            header: "Upload Failed",
            paragraph: "There was an error uploading your manifesto file.",
          });
          setIsErrorVisible(true);
          return;
        }
      }

      const sanitizedDescription = DOMPurify.sanitize(
        manifestoForm.description,
        {
          ALLOWED_TAGS: [],
        }
      );

      const manifestoData = {
        ...manifestoForm,
        fileUrl,
        description: sanitizedDescription,
        organizationID: getOrganizationId(),
      };

      let result;

      if (isEditMode) {
        // Send as array of objects for update
        const updatePayload = [{ ...manifestoData }];
        result = await dispatch(updateManifesto(updatePayload));
      } else {
        result = await dispatch(createManifesto(manifestoData));
      }

      if (result.error) {
        setErrorMessage({
          header: isEditMode ? "Update Failed" : "Creation Failed",
          paragraph:
            result.error.message ||
            `Failed to ${
              isEditMode ? "update" : "create"
            } manifesto. Please try again.`,
        });
        setIsErrorVisible(true);
        return;
      }

      if (!result.error) {
        setIsSuccessVisible(true);
      }
    } catch (error) {
      setErrorMessage({
        header: isEditMode ? "Update Error" : "Creation Error",
        paragraph:
          error.message || "An unexpected error occurred. Please try again.",
      });
      setIsErrorVisible(true);
    } finally {
      hideLoader();
    }
  };

  const handleSuccessClose = () => {
    setIsSuccessVisible(false);
    dispatch(resetManifestoForm());
    navigate("/dashboard/manifesto");
  };

  const handleErrorClose = () => {
    setIsErrorVisible(false);
  };

  // Clean up on unmount
  useEffect(() => {
    return () => {
      dispatch(resetManifestoForm());
    };
  }, [dispatch]);

  const handleInputChange = (field) => (e) => {
    dispatch(updateManifestoForm({ [field]: e.target.value }));
  };

  const handleEditorChange = (content) => {
    dispatch(updateManifestoForm({ description: content }));
  };

  const handleBack = () => {
    navigate("/dashboard/manifesto");
  };

  const isFormValid =
    Boolean(manifestoForm.name) &&
    Boolean(manifestoForm.description) &&
    Boolean(manifestoForm.fileUrl);

  // breadcrumb items
  const items = [
    { title: "Manifesto", link: "/dashboard/manifesto" },
    { title: "Add New", link: "" },
  ];

  return (
    <div className="w-full grid-cols-4 gap-10 py-6 text-left lg:grid sm:py-0">
      <div className="col-span-3 lg:border-r">
        <Breadcrumb items={items} />
        <div className="mb-8">
          <Back onClick={handleBack} />
        </div>

        <div className="my-5 rounded-lg lg:p-5 lg:border">
          <div className="mb-8">
            <h1 className="heading-medium text-lightestGrey">
              {isEditMode ? "Edit Manifesto" : "Create a New Manifesto"}
            </h1>
            <p className="font-normal text-lighterGrey">
              {isEditMode
                ? "Update the form below with your changes."
                : "Fill the form below. All fields are required to be filled with accurate information."}
            </p>
          </div>

          <div className="space-y-8">
            <section
              ref={(element) => {
                sectionRefs.current[0] = element;
              }}>
              <h2 className="mb-4 text-bold">
                Describe what this Manifesto is all about
              </h2>
              <div className="space-y-4">
                <Input
                  label="MANIFESTO TITLE"
                  placeholder="Enter manifesto title"
                  value={manifestoForm.name || ""}
                  onChange={handleInputChange("name")}
                />
                <TextEditor
                  label="MANIFESTO DESCRIPTION"
                  placeholder="Enter manifesto description"
                  value={manifestoForm.description || ""}
                  onChange={handleEditorChange}
                />
              </div>
            </section>

            <section
              ref={(element) => {
                sectionRefs.current[1] = element;
              }}>
              <h2 className="mb-4 text-bold">Media Upload</h2>
              <p className="mb-4 font-normal text-lighterGrey">
                Add up images to make your campaign easy to see
              </p>
              <FileInput
                id="manifesto-upload"
                label="UPLOAD FILE"
                placeholder={
                  manifestoForm.fileUrl
                    ? manifestoForm.fileUrl instanceof File
                      ? manifestoForm.fileUrl.name
                      : "File uploaded"
                    : "File Name.png"
                }
                onChange={handleFileUpload}
              />
            </section>

            {/* <section ref={sectionRefs[2]}>
              <h2 className="mb-4 text-bold">Link to Campaigns</h2>
              <p className="mb-4 font-normal text-lighterGrey">
                Select Campaigns that you&apos;d like this manifesto to be linked with
              </p>
              <AddPeopleCard
                title="ADD CAMPAIGNS"
                subtitle="No Campaigns selected. Click to select new"
                onClick={handleAddCampaigns}
              />
            </section> */}

            <div className="flex justify-end">
              <Button
                text="Proceed"
                onClick={handleSubmit}
                disabled={!isFormValid}
                color="primary"
                className={`
                w-full
                ${!isFormValid && "bg-darkBlue text-primary cursor-not-allowed"}
              `}
                customBgColor={isFormValid ? "primary" : "darkBlue"}
                customTextColor={isFormValid ? "white" : "darkBlue"}
                icon={<ArrowLeftOutlined className="rotate-180" />}
              />
            </div>
          </div>
        </div>

        <Success
          isVisible={isSuccessVisible}
          onClose={handleSuccessClose}
          header={isEditMode ? "Manifesto Updated" : "Manifesto Created"}
          paragraph={
            isEditMode
              ? "Your manifesto has been successfully updated."
              : "Your manifesto has been successfully created."
          }
          buttonText="Close"
        />

        <Error
          isVisible={isErrorVisible}
          onClose={handleErrorClose}
          header={errorMessage.header}
          paragraph={errorMessage.paragraph}
          buttonText="Try Again"
          recoveryPath="/dashboard/manifesto"
        />
      </div>
      <div className="hidden mt-10 lg:block h-[90vh] sticky top-[80px]">
        <Steps
          current={currentSection}
          steps={formSections}
          mode="single-page"
          sectionsCompleted={sectionsCompleted}
        />
      </div>
    </div>
  );
};

export default AddNewManifesto;
